import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * PublisherLogin — Femcare-branded standalone auth surface for third-party
 * publishers. Sessions are stored server-side in an HttpOnly cookie
 * `fc_pub_access` (JWT type=publisher_access) — completely separate from
 * Femcare admin and app-user sessions.
 */
export default function PublisherLogin() {
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [pw,    setPw]    = useState("");
    const [busy,  setBusy]  = useState(false);
    const [err,   setErr]   = useState(null);
    const API = process.env.REACT_APP_BACKEND_URL;

    // If already logged in, skip the form.
    useEffect(() => {
        fetch(`${API}/api/web/publisher/me`, { credentials: "include" })
            .then(r => r.ok && nav("/publisher", { replace: true }))
            .catch(() => {});
    }, [API, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null); setBusy(true);
        try {
            const r = await fetch(`${API}/api/web/publisher/auth/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: email.trim().toLowerCase(), password: pw }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.detail || "Login failed");
            nav("/publisher", { replace: true });
        } catch (e2) {
            setErr(e2.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-femcare-cream/40 px-4"
             data-testid="publisher-login-page">
            <div className="w-full max-w-md rounded-2xl bg-white border border-femcare-border p-8 shadow-sm">
                <div className="text-center mb-6">
                    <div className="text-xs uppercase tracking-widest text-femcare-plum font-semibold">Femcare</div>
                    <h1 className="text-2xl font-bold text-femcare-ink mt-1">Publisher Panel</h1>
                    <p className="text-sm text-femcare-ink/60 mt-2">
                        Sign in to see your Femcare traffic and confirmed conversions.
                    </p>
                </div>
                <form onSubmit={submit} className="space-y-4" data-testid="publisher-login-form">
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">Email</label>
                        <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                               required autoFocus autoComplete="email"
                               className="w-full rounded-xl border border-femcare-border px-4 py-2.5 text-sm focus:outline-none focus:border-femcare-plum"
                               data-testid="publisher-login-email" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider text-femcare-ink/60 mb-1">Password</label>
                        <input type="password" value={pw} onChange={(e)=>setPw(e.target.value)}
                               required autoComplete="current-password"
                               className="w-full rounded-xl border border-femcare-border px-4 py-2.5 text-sm focus:outline-none focus:border-femcare-plum"
                               data-testid="publisher-login-password" />
                    </div>
                    {err && (
                        <div className="rounded-xl bg-red-50 text-red-800 px-4 py-2.5 text-sm" data-testid="publisher-login-error">
                            {err}
                        </div>
                    )}
                    <button type="submit" disabled={busy}
                            className="w-full rounded-xl bg-femcare-plum text-white text-sm font-semibold py-3 disabled:opacity-50"
                            data-testid="publisher-login-submit">
                        {busy ? "Signing in…" : "Sign in"}
                    </button>
                </form>
                <p className="mt-6 text-center text-xs text-femcare-ink/50">
                    Femcare · Publisher access is provisioned by the Femcare team.
                </p>
            </div>
        </div>
    );
}
